import { useCallback, useEffect, useMemo } from "react";

import { faSave } from "@fortawesome/pro-regular-svg-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { isAxiosError } from "axios";
import { FormProvider, useForm } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import {
  useFloatingWindowsActions,
  useIsFloatingWindowPinned,
} from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { Icon } from "src/base-components/Icon";
import { Label } from "src/base-components/Label";
import { mdService } from "src/base-components/TextEditor/MarkdownService";
import { FloatingFormTitle } from "src/cases/caseView/components/FloatingFormWindow";
import { FormField } from "src/cases/caseView/components/FormField";
import {
  useAvailableCaseTasks,
  useCaseTaskFormSchema,
  useGetCaseTemplateTask,
  useTaskFormValidForCompletion,
} from "src/cases/caseView/hooks";
import { useCase, useSaveCaseTaskForm } from "src/cases/queries/cases";
import {
  CaseTask,
  TaskIdExtractor,
  transformCaseTask,
} from "src/cases/queries/casesTypes";
import { CaseTemplateTaskOutput } from "src/clients/case-management";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";

type TaskFormData = {
  task: CaseTask;
  templateTask: CaseTemplateTaskOutput;
  caseId: string;
  disabled?: boolean;
};

export const TaskFormFloatingWindow: React.FC<FloatingWindowProps> = ({
  id,
  computePosition,
  data,
}) => {
  const {
    task: initialTask,
    templateTask: fallbackTemplate,
    caseId,
    disabled,
  } = data as TaskFormData;

  const { close, setPin } = useFloatingWindowsActions();
  const isPinned = useIsFloatingWindowPinned(id);
  const { position, windowRef } = useFloatingWindowPosition(computePosition);

  const { data: caseData } = useCase(caseId);
  const availableTasks = useAvailableCaseTasks(caseData);
  const getTemplate = useGetCaseTemplateTask(caseData);

  const task = useMemo(() => {
    const updatedTask = availableTasks.find((t) => t.id === initialTask.id);
    const transformed = updatedTask
      ? transformCaseTask(updatedTask, getTemplate)
      : initialTask;

    if (
      JSON.stringify(transformed.formValues) ===
        JSON.stringify(initialTask.formValues) &&
      transformed.isCompleted === initialTask.isCompleted
    ) {
      return initialTask;
    }
    return transformed;
  }, [availableTasks, getTemplate, initialTask]);

  const template = getTemplate(task.key) || fallbackTemplate;

  const schema = useCaseTaskFormSchema(template);
  const { mutateAsync: saveTask, isPending } = useSaveCaseTaskForm(caseId);

  const defaultValues = useMemo(() => {
    const fields: Record<string, unknown> = {};

    template?.form?.fields?.forEach((field) => {
      fields[field.key] = field.type === "multi_select" ? [] : null;
    });

    return { ...fields, ...task.formValues };
  }, [template?.form?.fields, task.formValues]);

  const form = useForm<Record<string, unknown>>({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });

  const { isDirty, isValid } = form.formState;
  const canCompleteTask = useTaskFormValidForCompletion(
    template,
    task.formValues,
  );

  useEffect(() => {
    form.reset(defaultValues, { keepDirty: false, keepTouched: false });
  }, [defaultValues, form]);

  const closeWindow = useCallback(() => close(id), [close, id]);
  const togglePin = useCallback(
    () => setPin(id, !isPinned),
    [setPin, id, isPinned],
  );

  const saveForm = useCallback(async () => {
    if (!task.formMeta?.id) {
      logger.warn(`Task form ID is missing for task ${task.key}`);
      return;
    }

    const currentValues = form.getValues();
    form.reset(currentValues, { keepValues: true });

    try {
      const validatedValues = schema.cast(currentValues, {
        stripUnknown: true,
      }) as Record<string, unknown>;

      await saveTask({
        taskKey: TaskIdExtractor.getTaskKey(task.id),
        fullTaskId: task.id,
        formEtag: task.formMeta?.etag ?? 0,
        taskFormId: task.formMeta.id,
        taskFormValues: validatedValues,
      });
    } catch (error) {
      form.reset(currentValues, { keepValues: true, keepDirty: true });
      logger.error("Error saving case task form:", error);

      if (isPreconditionError(error)) {
        toastActions.failure({
          title: "Failed to save task form",
          description:
            "The task has been modified elsewhere. Please reload and try again.",
        });
      } else if (isAxiosError(error)) {
        toastActions.failure({
          title: "Failed to save task form",
          description: error.response?.data?.message,
        });
      } else {
        toastActions.failure({
          title: "Failed to save task form",
          description: "An unexpected error occurred. Please try again.",
        });
      }
      throw error;
    }
  }, [form, task, schema, saveTask]);

  const saveAndClose = useCallback(async () => {
    if (!isValid) return;

    if (isDirty) {
      await saveForm();
    }
    closeWindow();
  }, [isValid, isDirty, saveForm, closeWindow]);

  const formTitle = useMemo(() => {
    const hasForm = !!template?.form?.fields?.length;
    const checkboxDisabled =
      disabled || (hasForm && !task.isCompleted && !canCompleteTask);

    return (
      <FloatingFormTitle
        caseData={caseData}
        description={task.description}
        disabled={checkboxDisabled}
        task={task}
        title={task.title}
      />
    );
  }, [
    caseData,
    task,
    disabled,
    canCompleteTask,
    template?.form?.fields?.length,
  ]);

  const saveButton = useMemo(() => {
    const canSave = !isPending && isDirty && isValid;

    return (
      <Tooltip title="Save form">
        <div className="ml-2 flex h-4 w-4 items-center justify-center">
          <Icon
            color={
              canSave
                ? "text-indigo-500 hover:text-indigo-600"
                : "text-gray-400"
            }
            dataLoc="save-form"
            icon={faSave}
            size="xs"
            onClick={canSave ? saveForm : undefined}
          />
        </div>
      </Tooltip>
    );
  }, [isPending, isDirty, isValid, saveForm]);

  if (!template.form?.fields?.length) {
    return null;
  }

  const canSaveAndClose = !isPending && isDirty && isValid;

  return (
    <FloatingWindowInternal
      ref={windowRef}
      clipOverflow={false}
      draggable={true}
      headerAlignment="start"
      height="h-96"
      id={id}
      isPinned={isPinned}
      maximizable={false}
      resizeable={true}
      style={{
        inset: "0px auto auto 0px",
        transform: position,
      }}
      title={formTitle}
      titleRight={saveButton}
      onClose={closeWindow}
      onPin={togglePin}
    >
      <div className="relative -mx-2 flex h-full flex-col justify-between">
        <div className="decideScrollbar p-4">
          <FormProvider {...form}>
            <form className="flex flex-col gap-y-3">
              {template.form.fields.map((field) => (
                <div key={field.key}>
                  <Label
                    mb={field.description ? "mb-0" : "mb-2"}
                    required={field.required}
                  >
                    {field.display_name}
                  </Label>
                  {field.description && (
                    <div
                      //skipcq: JS-0440
                      dangerouslySetInnerHTML={{
                        __html: mdService.toHtml(field.description),
                      }}
                      className="tktl-markdown mb-2 font-inter-normal-12px text-gray-500"
                    />
                  )}
                  <FormField disabled={disabled} field={field} />
                  {form.formState.errors?.[field.key] && (
                    <ErrorHint>
                      {form.formState.errors[field.key]?.message}
                    </ErrorHint>
                  )}
                </div>
              ))}
            </form>
          </FormProvider>
        </div>

        <div>
          <div className="border-gray-100 border-t" />
          <div className="flex justify-end gap-2 px-4 pt-4 pb-2">
            <Button
              dataLoc="save-and-close-form"
              disabled={!canSaveAndClose}
              variant="primary"
              onClick={saveAndClose}
            >
              {isPending ? "Saving..." : "Save and Close"}
            </Button>
          </div>
        </div>
      </div>
    </FloatingWindowInternal>
  );
};
